import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Upload, Trash2, Save } from "lucide-react";
import { chatApi } from "./chatbotApi";
import "../PlaceholderPage.css";
import "./MaterialiGrafici.css";

// Pagina "Materiali grafici" del Chatbot (task 453), per admin e superadmin.
// Per ogni brand: colori, font, piè di pagina, descrizione (quella che il modello
// legge per capire QUANDO usare il brand) e la libreria degli asset (loghi, font).
// Raggiunta dal pulsante "Materiali" nella toolbar del chatbot; route hardcoded in
// App.js nel blocco admin, come /upload e /gestione-utenti.

const KIND_LABELS = {
    logo_colori: "Logo a colori",
    logo_bianco: "Logo bianco",
    logo_nero: "Logo nero",
    logo_negativo: "Logo negativo",
    logo_social: "Logo social (bianco, senza claim)",
    logo_reel: "Logo per storie/reel (verticale, bianco)",
    logo_lettera: "Logo per la carta intestata",
    firma: "Firma autografa (la descrizione dice di chi è)",
    sfondo_citazione: "Sfondo social · citazione",
    sfondo_informazione: "Sfondo social · informazione",
    sfondo_dato: "Sfondo social · dato",
    sfondo_reel: "Sfondo social · reel (9:16)",
    sfondo_copertina: "Copertina Facebook (1640×624)",
    sfondo_carosello_copertina: "Carosello · copertina (velo sopra la foto)",
    sfondo_carosello_testo: "Carosello · slide di testo",
    sfondo_carosello_foto: "Carosello · slide foto (banda in basso)",
    sfondo_carosello_chiusura: "Carosello · chiusura",
    font: "Font",
    immagine: "Immagine",
    altro: "Altro",
};

// Campi del brand modificabili dal pannello, nell'ordine del form.
const CAMPI_COLORE = [
    ["colore_primario", "Primario"],
    ["colore_secondario", "Secondario"],
    ["colore_accento", "Accento"],
];

const formatBytes = (n) => (!n ? "" : n < 1024 * 1024 ? `${Math.round(n / 1024)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);

const draftOf = (b) => ({
    nome: b.nome || "",
    descrizione: b.descrizione || "",
    colore_primario: b.colore_primario,
    colore_secondario: b.colore_secondario,
    colore_accento: b.colore_accento,
    font: b.font || "",
    footer_text: b.footer_text || "",
});

// Una card per brand: dati + asset + upload. Lo stato del form è locale alla card.
const BrandCard = ({ brand, onChanged, notify }) => {
    const [draft, setDraft] = useState(draftOf(brand));
    const [saving, setSaving] = useState(false);
    const [file, setFile] = useState(null);
    const [kind, setKind] = useState("logo_colori");
    const [descrizione, setDescrizione] = useState("");
    const [uploading, setUploading] = useState(false);
    const [confirmId, setConfirmId] = useState(null);
    const fileInputId = `cm-file-${brand.key}`;

    // Se il brand cambia da fuori (ricarica dopo un salvataggio) il form si riallinea.
    useEffect(() => setDraft(draftOf(brand)), [brand]);

    const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
    const dirty = JSON.stringify(draft) !== JSON.stringify(draftOf(brand));

    const save = async () => {
        setSaving(true);
        try {
            await chatApi.updateBrand(brand.key, draft);
            notify("success", `${brand.nome}: modifiche salvate`);
            await onChanged();
        } catch (e) {
            notify("error", e.response?.data?.error || "Salvataggio non riuscito");
        } finally {
            setSaving(false);
        }
    };

    const upload = async (e) => {
        e.preventDefault();
        if (!file) return;
        setUploading(true);
        try {
            await chatApi.uploadBrandAsset(brand.key, file, kind, descrizione);
            notify("success", `${file.name} caricato`);
            setFile(null);
            setDescrizione("");
            const input = document.getElementById(fileInputId);
            if (input) input.value = "";
            await onChanged();
        } catch (e2) {
            notify("error", e2.response?.data?.error || "Caricamento non riuscito");
        } finally {
            setUploading(false);
        }
    };

    const remove = async (asset) => {
        try {
            await chatApi.deleteBrandAsset(brand.key, asset.id);
            notify("success", `${asset.filename} eliminato`);
            setConfirmId(null);
            await onChanged();
        } catch (e) {
            notify("error", e.response?.data?.error || "Eliminazione non riuscita");
        }
    };

    const isImage = (a) => a.kind.startsWith("logo_") || a.kind.startsWith("sfondo_") || ["immagine", "firma"].includes(a.kind);
    // Loghi bianchi e negativi su bianco non si vedono: anteprima su fondo scuro.
    const darkPreview = (a) => ["logo_bianco", "logo_negativo", "logo_social", "logo_reel"].includes(a.kind);

    return (
        <section className="cm-card">
            <div className="cm-card-head">
                <h3>
                    <span className="cm-swatch-dot" style={{ background: brand.colore_primario }} />
                    {brand.nome} <code>{brand.key}</code>
                </h3>
            </div>

            <div className="cm-fields">
                <label className="cm-field">
                    <span>Nome</span>
                    <input value={draft.nome} onChange={(e) => set("nome", e.target.value)} maxLength={80} />
                </label>
                <label className="cm-field">
                    <span>Font</span>
                    <input value={draft.font} onChange={(e) => set("font", e.target.value)} maxLength={60} placeholder="Helvetica" />
                </label>
                {CAMPI_COLORE.map(([k, label]) => (
                    <label key={k} className="cm-field cm-field-color">
                        <span>{label}</span>
                        <div className="cm-swatch">
                            <input type="color" value={draft[k]} onChange={(e) => set(k, e.target.value.toUpperCase())} />
                            <input
                                type="text"
                                value={draft[k]}
                                onChange={(e) => set(k, e.target.value.toUpperCase())}
                                maxLength={7}
                                pattern="#[0-9A-Fa-f]{6}"
                                spellCheck={false}
                            />
                        </div>
                    </label>
                ))}
                <label className="cm-field cm-field-wide">
                    <span>Piè di pagina (contatti)</span>
                    <input
                        value={draft.footer_text}
                        onChange={(e) => set("footer_text", e.target.value)}
                        placeholder="Es. La Fraternita Soc. Coop. Sociale — via … — tel … — www.…   (vuoto = nessun piè di pagina)"
                    />
                </label>
                <label className="cm-field cm-field-wide">
                    <span>Quando l'assistente deve usare questo brand</span>
                    <textarea rows={2} value={draft.descrizione} onChange={(e) => set("descrizione", e.target.value)} />
                </label>
                <div className="cm-field-actions">
                    <button className="cm-btn cm-btn-primary" onClick={save} disabled={!dirty || saving}>
                        <Save size={14} /> {saving ? "Salvataggio…" : "Salva"}
                    </button>
                </div>
            </div>

            <h4 className="cm-assets-title">Materiali ({brand.assets.length})</h4>
            <p className="cm-hint">
                Nei documenti viene usato l'<b>ultimo</b> logo a colori caricato (e l'ultimo logo bianco nelle bande
                colorate del volantino): per sostituirne uno basta caricarne uno nuovo. I loghi vengono ridimensionati a
                max 1600 px e convertiti in PNG. Le grafiche social usano il <b>logo social</b> (o il logo bianco, se
                manca) e gli <b>sfondi social</b> del formato: 1080×1350 per i post, 1080×1920 per i reel. Un formato
                senza sfondo riceve una sfumatura generata dai colori del brand.
            </p>
            <div className="cm-assets">
                {brand.assets.map((a) => (
                    <div key={a.id} className="cm-asset">
                        <div className={`cm-asset-preview ${darkPreview(a) ? "cm-asset-dark" : ""}`}>
                            {isImage(a) && a.url ? (
                                <img src={a.url} alt={a.filename} loading="lazy" />
                            ) : a.kind === "font" ? (
                                <span className="cm-asset-font">Aa</span>
                            ) : (
                                <span className="cm-asset-ext">{(a.filename.split(".").pop() || "").toUpperCase()}</span>
                            )}
                        </div>
                        <div className="cm-asset-kind">{KIND_LABELS[a.kind] || a.kind}</div>
                        <div className="cm-asset-name" title={a.filename}>
                            {a.filename}
                        </div>
                        {a.descrizione && <div className="cm-asset-desc">{a.descrizione}</div>}
                        <div className="cm-asset-foot">
                            <span>{formatBytes(a.size)}</span>
                            {confirmId === a.id ? (
                                <span className="cm-confirm">
                                    Eliminare?
                                    <button className="cm-link cm-link-danger" onClick={() => remove(a)}>Sì</button>
                                    <button className="cm-link" onClick={() => setConfirmId(null)}>No</button>
                                </span>
                            ) : (
                                <button className="cm-icon-btn" onClick={() => setConfirmId(a.id)} title="Elimina">
                                    <Trash2 size={14} />
                                </button>
                            )}
                        </div>
                    </div>
                ))}
                {brand.assets.length === 0 && <div className="cm-empty">Nessun materiale caricato.</div>}
            </div>

            <form className="cm-upload" onSubmit={upload}>
                <input
                    id={fileInputId}
                    type="file"
                    accept=".png,.jpg,.jpeg,.webp,.ttf,.otf"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
                <select value={kind} onChange={(e) => setKind(e.target.value)}>
                    {Object.entries(KIND_LABELS).map(([k, label]) => (
                        <option key={k} value={k}>{label}</option>
                    ))}
                </select>
                <input
                    className="cm-upload-desc"
                    value={descrizione}
                    onChange={(e) => setDescrizione(e.target.value)}
                    placeholder="Descrizione (es. logo orizzontale, da usare su sfondo chiaro)"
                />
                <button className="cm-btn cm-btn-primary" type="submit" disabled={!file || uploading}>
                    <Upload size={14} /> {uploading ? "Caricamento…" : "Carica"}
                </button>
            </form>
        </section>
    );
};

const MaterialiGrafici = () => {
    const navigate = useNavigate();
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [msg, setMsg] = useState(null); // { type: 'error'|'success', text }

    const notify = useCallback((type, text) => {
        setMsg({ type, text });
        if (type === "success") setTimeout(() => setMsg((m) => (m?.text === text ? null : m)), 3000);
    }, []);

    const load = useCallback(async () => {
        try {
            setBrands(await chatApi.listBrands());
        } catch (e) {
            notify("error", e.response?.data?.error || "Impossibile caricare i materiali");
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <div className="placeholder-container chatbot-materiali">
            <header className="page-header">
                <div className="header-content">
                    <button className="cm-back" onClick={() => navigate("/chatbot-ai")} title="Torna al chatbot">
                        <ArrowLeft size={18} />
                        <span>Chatbot</span>
                    </button>
                    <div className="header-title">
                        <h2>Materiali grafici</h2>
                        <p className="header-subtitle">
                            Loghi, colori, font, sfondi e piè di pagina dei brand che l'assistente applica a documenti
                            (Word, PowerPoint, PDF, volantini) e grafiche social. Nessun brand è predefinito: se la richiesta
                            non dice per quale brand, l'assistente lo chiede
                        </p>
                    </div>
                </div>
            </header>

            <div className="content-area cm-content">
                {msg && <div className={`cm-msg cm-msg-${msg.type}`}>{msg.text}</div>}
                {loading ? (
                    <div className="cm-empty">Caricamento…</div>
                ) : (
                    <div className="cm-grid">
                        {brands.map((b) => (
                            <BrandCard key={b.key} brand={b} onChanged={load} notify={notify} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MaterialiGrafici;
